import { useState, useEffect, useRef } from 'react';
import { ShieldCheck, Upload, FileText, BookOpen, Loader2, ChevronDown, X, AlertCircle, Download, Save, Check, Clapperboard } from 'lucide-react';

const API = '/api';
function getToken() { return localStorage.getItem('token'); }

const RATING_CONFIG = {
  S: { label: 'S 级爆款', bg: 'bg-[#f2eadb] dark:bg-white/[0.08]', text: 'text-[#7f6b45] dark:text-[#d4c7a8]', border: 'border-[#e0d3b9] dark:border-white/[0.12]', dot: 'bg-[#c2a079]' },
  A: { label: 'A 级优质', bg: 'bg-[#eee9de] dark:bg-white/[0.08]', text: 'text-[#6f6196] dark:text-[#b9afe7]', border: 'border-[#c9bedc] dark:border-white/[0.12]', dot: 'bg-[#8c7db0]' },
  B: { label: 'B 级良好', bg: 'bg-[#e7eee8] dark:bg-white/[0.08]', text: 'text-[#5e7c76] dark:text-[#9fc6bd]', border: 'border-[#c7d8cf] dark:border-white/[0.12]', dot: 'bg-[#7fafa8]' },
  C: { label: 'C 级需改', bg: 'bg-red-50 dark:bg-red-500/10',       text: 'text-red-600 dark:text-red-400',       border: 'border-red-300 dark:border-red-500/40',      dot: 'bg-red-400' },
};

const CONCLUSION_COLOR = {
  '能拍':     'text-[#5e7c76] dark:text-[#9fc6bd]',
  '值得优化': 'text-[#7f6b45] dark:text-[#d4c7a8]',
  '不建议拍': 'text-red-600 dark:text-red-400',
};

function ReportDisplay({ report, meta }) {
  if (!report) return null;
  const rc = RATING_CONFIG[meta?.rating] || RATING_CONFIG.B;
  const conclusionColor = Object.entries(CONCLUSION_COLOR).find(([k]) => meta?.conclusion?.includes(k))?.[1] || 'text-gray-700 dark:text-gray-300';

  // Split report into paragraphs preserving headers
  const lines = report.split('\n');

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className={`rounded-2xl border-2 p-5 ${rc.border} ${rc.bg}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-3xl font-black ${rc.text}`}>{meta?.rating}</span>
              <span className={`text-sm font-semibold ${rc.text}`}>{rc.label}</span>
              {meta?.score > 0 && <span className="text-sm text-gray-400">· {meta.score} 分</span>}
            </div>
            <p className={`text-sm font-semibold ${conclusionColor}`}>
              结论：{meta?.conclusion || '—'}
            </p>
          </div>
          {meta?.ai_percentage != null && (
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">{meta.ai_percentage}%</div>
              <div className="text-xs text-gray-400">AI 含量</div>
            </div>
          )}
        </div>
      </div>

      {/* Full report as formatted text */}
      <div className="card p-6">
        <div className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 leading-relaxed">
          {lines.map((line, i) => {
            if (line.startsWith('### ')) {
              return <h3 key={i} className="text-sm font-bold text-gray-900 dark:text-gray-100 mt-5 mb-2 first:mt-0">{line.replace('### ', '')}</h3>;
            }
            if (line.startsWith('## ')) {
              return <h2 key={i} className="text-base font-bold text-gray-900 dark:text-gray-100 mt-6 mb-2 first:mt-0">{line.replace('## ', '')}</h2>;
            }
            if (line.startsWith('#### ')) {
              return <h4 key={i} className="text-xs font-bold text-gray-700 dark:text-gray-300 mt-4 mb-1 uppercase tracking-wide">{line.replace('#### ', '')}</h4>;
            }
            if (line.startsWith('**') && line.endsWith('**') && line.length > 4) {
              return <p key={i} className="font-semibold text-gray-900 dark:text-gray-100 my-1">{line.replace(/\*\*/g, '')}</p>;
            }
            if (line.startsWith('- ') || line.startsWith('• ')) {
              const content = line.replace(/^[-•] /, '');
              const formatted = content.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
              return <div key={i} className="flex gap-2 my-0.5 text-sm"><span className="text-[#8c7db0] shrink-0 mt-0.5">·</span><span dangerouslySetInnerHTML={{ __html: formatted }} /></div>;
            }
            if (line.match(/^⸻+$/)) {
              return <hr key={i} className="border-gray-100 dark:border-white/[0.06] my-3" />;
            }
            if (line.match(/^━+$/)) {
              return <hr key={i} className="border-[#ded6c8] dark:border-white/[0.1] my-4 border-2" />;
            }
            if (!line.trim()) return <div key={i} className="h-1" />;
            const formatted = line.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
            return <p key={i} className="text-sm my-0.5 leading-relaxed" dangerouslySetInnerHTML={{ __html: formatted }} />;
          })}
        </div>
      </div>
    </div>
  );
}

export default function Review() {
  const [mode, setMode]         = useState('text'); // 'script' | 'upload' | 'text'
  const [scripts, setScripts]   = useState([]);
  const [scriptId, setScriptId] = useState('');
  const [title, setTitle]       = useState('');
  const [text, setText]         = useState('');
  const [file, setFile]         = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [result, setResult]       = useState(null);
  const [error, setError]         = useState('');
  const [history, setHistory]     = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [scriptContent, setScriptContent] = useState('');
  const [saved, setSaved]         = useState(false);
  const [saving, setSaving]       = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    fetch(`${API}/scripts`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json()).then(d => setScripts(Array.isArray(d) ? d : d.scripts || [])).catch(() => {});
    fetch(`${API}/review/history`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json()).then(d => Array.isArray(d) && setHistory(d)).catch(() => {});
  }, []);

  const handleFile = f => {
    if (!f) return;
    if (!f.name.match(/\.(pdf|docx|doc|txt)$/i)) { setError('仅支持 PDF / DOCX / TXT 格式'); return; }
    setFile(f);
    setTitle(f.name.replace(/\.[^.]+$/, ''));
    setError('');
  };

  const submit = async () => {
    setError(''); setResult(null); setLoading(true); setSaved(false); setScriptContent('');
    try {
      let res;
      if (mode === 'script') {
        if (!scriptId) { setError('请选择剧本'); setLoading(false); return; }
        res = await fetch(`${API}/review/script/${scriptId}`, {
          method: 'POST', headers: { Authorization: `Bearer ${getToken()}` },
        });
      } else if (mode === 'upload') {
        if (!file) { setError('请上传文件'); setLoading(false); return; }
        const fd = new FormData();
        fd.append('file', file);
        fd.append('title', title);
        res = await fetch(`${API}/review/upload`, {
          method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
        });
      } else {
        if (!text.trim()) { setError('请输入剧本内容'); setLoading(false); return; }
        res = await fetch(`${API}/review/text`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: title || '未命名剧本', text }),
        });
      }
      const data = await res.json();
      if (!res.ok || data.error) { setError(data.error || '审核失败'); return; }
      setResult(data);
      // 记录可保存的脚本内容
      if (mode === 'upload' && data.extracted_text) setScriptContent(data.extracted_text);
      if (mode === 'text') setScriptContent(text);
      // Refresh history
      fetch(`${API}/review/history`, { headers: { Authorization: `Bearer ${getToken()}` } })
        .then(r => r.json()).then(d => Array.isArray(d) && setHistory(d)).catch(() => {});
    } catch (e) {
      setError(e.message || '网络错误');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async item => {
    setShowHistory(false);
    if (item.report) {
      const meta = { rating: item.rating, score: item.score, conclusion: item.conclusion, ai_percentage: item.ai_percentage };
      setResult({ report: item.report, meta });
    } else {
      const r = await fetch(`${API}/review/result/${item.id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const d = await r.json();
      if (d.report) setResult({ report: d.report, meta: { rating: d.rating, score: d.score, conclusion: d.conclusion, ai_percentage: d.ai_percentage } });
    }
  };

  const downloadPDF = () => {
    const rc = RATING_CONFIG[result?.meta?.rating] || RATING_CONFIG.B;
    const reportHtml = (result?.report || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/^[-•] (.+)$/gm, '<li>$1</li>')
      .replace(/^⸻+$/gm, '<hr/>')
      .replace(/^━+$/gm, '<hr class="thick"/>')
      .replace(/\n/g, '<br/>');
    const html = `<!DOCTYPE html><html lang="zh-CN"><head><meta charset="UTF-8"/>
      <title>${title || '剧本审核报告'}</title>
      <style>
        body{font-family:-apple-system,sans-serif;max-width:820px;margin:0 auto;padding:48px 40px;color:#1a1a1a;font-size:14px;line-height:1.8}
        .header{border-bottom:2px solid #7c3aed;padding-bottom:16px;margin-bottom:24px}
        .header h1{margin:0 0 4px;font-size:22px}.header p{margin:0;color:#6b7280;font-size:12px}
        .summary{background:#f5f3ff;border:2px solid #c4b5fd;border-radius:12px;padding:20px;margin:20px 0;display:flex;justify-content:space-between;align-items:center}
        .rating{font-size:40px;font-weight:900;color:#7c3aed;line-height:1}.rating-label{font-size:15px;color:#7c3aed;font-weight:600}
        .conclusion{font-size:13px;margin-top:4px;color:#b45309;font-weight:600}
        .ai-pct{text-align:center}.ai-num{font-size:28px;font-weight:700;color:#374151}.ai-label{font-size:11px;color:#9ca3af}
        h2{font-size:15px;font-weight:700;border-left:3px solid #7c3aed;padding-left:10px;margin:28px 0 10px}
        h3{font-size:14px;font-weight:700;margin:20px 0 8px;color:#374151}
        h4{font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#6b7280;margin:16px 0 6px}
        li{margin:3px 0;padding-left:4px}strong{color:#1a1a1a}
        hr{border:none;border-top:1px solid #e5e7eb;margin:16px 0}hr.thick{border-top:2px solid #d1d5db}
        .footer{margin-top:40px;padding-top:16px;border-top:1px solid #e5e7eb;font-size:11px;color:#9ca3af;text-align:center}
        @media print{body{padding:24px}@page{margin:20mm}}
      </style></head><body>
      <div class="header"><h1>${title || '剧本审核报告'}</h1>
        <p>生成时间：${new Date().toLocaleString('zh-CN')} &nbsp;·&nbsp; 小影 AI 剧本审核系统</p></div>
      <div class="summary">
        <div><div class="rating">${result?.meta?.rating || 'B'}</div>
          <div class="rating-label">${rc.label}${result?.meta?.score > 0 ? ' · ' + result.meta.score + ' 分' : ''}</div>
          <div class="conclusion">结论：${result?.meta?.conclusion || '—'}</div></div>
        ${result?.meta?.ai_percentage != null ? `<div class="ai-pct"><div class="ai-num">${result.meta.ai_percentage}%</div><div class="ai-label">AI 含量</div></div>` : ''}
      </div>
      <div>${reportHtml}</div>
      <div class="footer">本报告由小影 AI 剧本审核系统生成 &nbsp;·&nbsp; 扬州红影智投文化科技有限公司</div>
      </body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  const saveToLibrary = async () => {
    if (!scriptContent || saving || saved) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/scripts/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title || '未命名剧本', content: scriptContent }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error);
      setSaved(true);
    } catch (e) {
      setError(e.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const MODES = [
    { id: 'text',   icon: <FileText size={15} />,  label: '粘贴文本' },
    { id: 'upload', icon: <Upload size={15} />,    label: '上传文件' },
    { id: 'script', icon: <BookOpen size={15} />,  label: '平台剧本' },
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="page-header flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} className="text-[#6f6196]" />
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">审核报告</h1>

        </div>
        <button onClick={() => setShowHistory(v => !v)}
          className="btn-secondary text-sm flex items-center gap-1.5">
          历史记录 ({history.length})
          <ChevronDown size={13} className={`transition-transform ${showHistory ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* ── Left: Input panel ── */}
        <div className="w-[420px] shrink-0 flex flex-col border-r border-[#ded6c8] dark:border-white/[0.06] overflow-y-auto bg-[#fbfaf6] dark:bg-[#0c0c10]">
          <div className="p-5 space-y-4">
            {/* Mode tabs */}
            <div className="flex gap-1 bg-[#eee9de] dark:bg-white/[0.05] p-1 rounded-xl">
              {MODES.map(m => (
                <button key={m.id} onClick={() => { setMode(m.id); setResult(null); setError(''); setSaved(false); setScriptContent(''); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all ${
                    mode === m.id
                      ? 'bg-[#fbfaf6] dark:bg-[#1a1a24] text-[#6f6196] dark:text-[#b9afe7] shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}>
                  {m.icon}{m.label}
                </button>
              ))}
            </div>

            {/* Title input (for text/upload modes) */}
            {mode !== 'script' && (
              <input className="input text-sm" placeholder="剧本标题（选填）"
                value={title} onChange={e => setTitle(e.target.value)} />
            )}

            {/* Script selector */}
            {mode === 'script' && (
              <div className="relative">
                <select className="input text-sm appearance-none pr-8" value={scriptId} onChange={e => setScriptId(e.target.value)}>
                  <option value="" className="bg-white text-stone-800 dark:bg-[#1a1a22] dark:text-gray-100">选择剧本管理中的剧本…</option>
                  {scripts.map(s => <option key={s.id} value={s.id} className="bg-white text-stone-800 dark:bg-[#1a1a22] dark:text-gray-100">{s.title || s.idea?.slice(0, 30)}</option>)}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            )}

            {/* File upload */}
            {mode === 'upload' && (
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
                onClick={() => fileRef.current?.click()}
                className={`relative border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
                  dragOver ? 'border-[#8c7db0] bg-[#eee9de] dark:bg-white/[0.08]'
                  : file ? 'border-[#7fafa8] bg-[#e7eee8] dark:bg-white/[0.08]'
                  : 'border-[#ded6c8] dark:border-white/[0.1] hover:border-[#c9bedc]'
                }`}>
                <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt" className="hidden"
                  onChange={e => handleFile(e.target.files[0])} />
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText size={20} className="text-[#5e7c76]" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{file.name}</p>
                      <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(0)} KB</p>
                    </div>
                    <button onClick={e => { e.stopPropagation(); setFile(null); }}
                      className="ml-2 text-gray-400 hover:text-red-500 transition-colors">
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload size={24} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                    <p className="text-sm text-gray-500">拖拽或点击上传</p>
                    <p className="text-xs text-gray-400 mt-1">支持 PDF · DOCX · TXT，最大 20MB</p>
                  </>
                )}
              </div>
            )}

            {/* Text input */}
            {mode === 'text' && (
              <textarea
                className="input text-sm resize-none"
                style={{ minHeight: 300 }}
                placeholder="在此粘贴完整剧本内容…&#10;&#10;支持分集大纲、完整剧本、剧情简介等任意格式"
                value={text}
                onChange={e => setText(e.target.value)}
              />
            )}

            {error && (
              <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl px-4 py-3">
                <AlertCircle size={15} className="shrink-0 mt-0.5" />
                {error}
              </div>
            )}

            <button onClick={submit} disabled={loading}
              className="btn-primary w-full py-3 flex items-center justify-center gap-2">
              {loading
                ? <><Loader2 size={16} className="animate-spin" />AI 审核中，约 30-60 秒…</>
                : <><ShieldCheck size={16} />开始审核</>}
            </button>

            {loading && (
              <p className="text-xs text-center text-gray-400">
                AI 正在精读剧本，请耐心等待…
              </p>
            )}
          </div>
        </div>

        {/* ── Right: Results + History ── */}
        <div className="flex-1 overflow-y-auto">
          {/* History drawer */}
          {showHistory && (
            <div className="border-b border-[#ded6c8] dark:border-white/[0.06] bg-[#f4f1e9] dark:bg-[#0c0c10]">
              <div className="p-4 grid grid-cols-1 gap-2 max-h-64 overflow-y-auto">
                {history.length === 0 && <p className="text-sm text-gray-400 text-center py-4">暂无历史记录</p>}
                {history.map(item => {
                  const rc = RATING_CONFIG[item.rating] || RATING_CONFIG.B;
                  return (
                    <button key={item.id} onClick={() => loadHistory(item)}
                      className="flex items-center gap-3 p-3 rounded-xl text-left hover:bg-[#fbfaf6] dark:hover:bg-white/[0.04] transition-colors border border-transparent hover:border-[#ded6c8] dark:hover:border-white/[0.07]">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${rc.bg} ${rc.text} shrink-0`}>
                        {item.rating}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                          {item.script_title || item.file_name || '平台剧本'}
                        </p>
                        <p className="text-xs text-gray-400">{item.conclusion} · {new Date(item.created_at).toLocaleDateString('zh-CN')}</p>
                      </div>
                      <span className={`text-xs font-medium ${rc.text} shrink-0`}>{item.score > 0 ? `${item.score}分` : ''}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty state */}
          {!result && !loading && (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-8">
              <div className="w-16 h-16 rounded-2xl bg-[#eee9de] dark:bg-white/[0.08] border border-[#ded6c8] dark:border-white/[0.12] flex items-center justify-center text-[#6f6196] mb-4"><Clapperboard size={28} /></div>
              <h3 className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">专业剧本审核</h3>
              <p className="text-sm text-gray-400 leading-relaxed max-w-sm">
                基于短剧行业标准，从钩子强度、人物冲突、爽点有效性等维度深度评估，给出 S/A/B/C 评级和可执行修改建议。
              </p>
            </div>
          )}

          {/* Loading animation */}
          {loading && (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
              <div className="w-12 h-12 border-2 border-[#8c7db0]/30 border-t-[#8c7db0] rounded-full animate-spin" />
              <div className="text-center">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">正在精读剧本…</p>
                <p className="text-xs text-gray-400 mt-1">深度分析中，请稍候…</p>
              </div>
            </div>
          )}

          {/* Results */}
          {result && !loading && (
            <div className="p-6">
              {/* Action buttons */}
              <div className="flex gap-2 mb-5 justify-end">
                {mode !== 'script' && scriptContent && (
                  <button onClick={saveToLibrary} disabled={saving || saved}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                      saved
                        ? 'bg-[#e7eee8] dark:bg-white/[0.08] text-[#5e7c76] dark:text-[#9fc6bd] border border-[#c7d8cf] dark:border-white/[0.12]'
                        : 'bg-[#fbfaf6] dark:bg-white/[0.05] border border-[#ded6c8] dark:border-white/[0.08] text-gray-600 dark:text-gray-400 hover:border-[#c9bedc] hover:text-[#6f6196]'
                    }`}>
                    {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <Check size={14} /> : <Save size={14} />}
                    {saved ? '已保存到剧本管理' : saving ? '保存中…' : '保存到剧本管理'}
                  </button>
                )}
                <button onClick={downloadPDF}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium bg-[#8c7db0] hover:bg-[#7d6ca3] text-white transition-colors">
                  <Download size={14} />下载 PDF 报告
                </button>
              </div>
              <ReportDisplay report={result.report} meta={result.meta} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
